import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, Upload, X, Check, Palette, ArrowRight, ArrowLeft,
  Building2, Mail, Phone, FileText, Sparkles, Rocket, Globe
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Local translation helper for component-level translations
const getTranslation = (content, language) => {
  if (content[language]) return content[language];
  if (content['en']) return content['en'];
  if (content['tr']) return content['tr'];
  return content[Object.keys(content)[0]];
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const colorPresets = [
  { name: 'Cyan', primary: '#38BDF8', background: '#030303', accent: '#0EA5E9' },
  { name: 'Purple', primary: '#A855F7', background: '#030303', accent: '#9333EA' },
  { name: 'Emerald', primary: '#10B981', background: '#030303', accent: '#059669' },
  { name: 'Orange', primary: '#F97316', background: '#030303', accent: '#EA580C' },
  { name: 'Rose', primary: '#F43F5E', background: '#030303', accent: '#E11D48' },
  { name: 'Gold', primary: '#EAB308', background: '#030303', accent: '#CA8A04' },
];

const websiteTypes = [
  { id: 'landing', icon: Rocket, labelEn: 'Landing Page', labelTr: 'Landing Page' },
  { id: 'corporate', icon: Building2, labelEn: 'Corporate Site', labelTr: 'Kurumsal Site' },
  { id: 'ecommerce', icon: Globe, labelEn: 'E-Commerce', labelTr: 'E-Ticaret' },
  { id: 'portfolio', icon: Sparkles, labelEn: 'Portfolio', labelTr: 'Portfolyo' },
];

// Translations
const translations = {
  en: {
    step1Title: 'What type of website do you need?',
    step1Subtitle: 'Select the option that best fits your project',
    step2Title: 'Tell us about your brand',
    step2Subtitle: 'We\'ll create a custom design for you',
    step3Title: 'Choose your style',
    step3Subtitle: 'Pick colors that represent your brand',
    brandLabel: 'Brand / Company Name',
    brandPlaceholder: 'Enter your business name',
    emailLabel: 'Email Address',
    emailPlaceholder: 'you@company.com',
    phoneLabel: 'Phone (Optional)',
    phonePlaceholder: '+1 (555) 000-0000',
    descLabel: 'Tell us more (Optional)',
    descPlaceholder: 'What does your business do? Who is your target audience?',
    logoLabel: 'Upload Logo (Optional)',
    logoHint: 'PNG, JPG or SVG - Max 5MB',
    dragDrop: 'Drag & drop or click to upload',
    colorPresets: 'Quick Presets',
    customColors: 'Or customize',
    primaryColor: 'Primary',
    bgColor: 'Background', 
    accentColor: 'Accent',
    next: 'Continue',
    back: 'Back',
    submit: 'Submit Request',
    submitting: 'Sending...',
    successTitle: 'Request Submitted!',
    successText: 'We\'ll prepare your custom template and contact you within 24-48 hours.',
    successCta: 'Done',
    errorText: 'Something went wrong. Please try again.',
    step: 'Step',
    of: 'of'
  },
  tr: {
    step1Title: 'Ne tür bir web sitesine ihtiyacınız var?',
    step1Subtitle: 'Projenize en uygun seçeneği seçin',
    step2Title: 'Markanız hakkında bilgi verin',
    step2Subtitle: 'Size özel bir tasarım oluşturacağız',
    step3Title: 'Stilinizi seçin',
    step3Subtitle: 'Markanızı temsil eden renkleri seçin',
    brandLabel: 'Marka / Şirket Adı',
    brandPlaceholder: 'İşletmenizin adını girin',
    emailLabel: 'E-posta Adresi',
    emailPlaceholder: 'siz@sirket.com',
    phoneLabel: 'Telefon (İsteğe bağlı)',
    phonePlaceholder: '+90 (555) 000 0000',
    descLabel: 'Daha fazla bilgi verin (İsteğe bağlı)',
    descPlaceholder: 'İşletmeniz ne yapıyor? Hedef kitleniz kim?',
    logoLabel: 'Logo Yükleyin (İsteğe bağlı)',
    logoHint: 'PNG, JPG veya SVG - Max 5MB',
    dragDrop: 'Sürükle bırak veya tıklayarak yükle',
    colorPresets: 'Hızlı Şablonlar',
    customColors: 'Veya özelleştir',
    primaryColor: 'Ana Renk',
    bgColor: 'Arkaplan',
    accentColor: 'Vurgu',
    next: 'Devam',
    back: 'Geri',
    submit: 'Talebi Gönder',
    submitting: 'Gönderiliyor...',
    successTitle: 'Talebiniz Alındı!',
    successText: 'Size özel şablonunuzu hazırlayıp 24-48 saat içinde iletişime geçeceğiz.',
    successCta: 'Tamam',
    errorText: 'Bir hata oluştu. Lütfen tekrar deneyin.',
    step: 'Adım',
    of: '/'
  },
  es: {
    step1Title: '¿Qué tipo de sitio web necesitas?',
    step1Subtitle: 'Selecciona la opción que mejor se adapte a tu proyecto',
    step2Title: 'Cuéntanos sobre tu marca',
    step2Subtitle: 'Crearemos un diseño personalizado para ti',
    step3Title: 'Elige tu estilo',
    step3Subtitle: 'Selecciona colores que representen tu marca',
    brandLabel: 'Nombre de Marca / Empresa',
    brandPlaceholder: 'Ingresa el nombre de tu negocio',
    emailLabel: 'Correo Electrónico',
    emailPlaceholder: 'tu@empresa.com',
    phoneLabel: 'Teléfono (Opcional)',
    phonePlaceholder: '+34 600 000 000',
    descLabel: 'Cuéntanos más (Opcional)',
    descPlaceholder: '¿Qué hace tu negocio? ¿Quién es tu público objetivo?',
    logoLabel: 'Subir Logo (Opcional)',
    logoHint: 'PNG, JPG o SVG - Max 5MB',
    dragDrop: 'Arrastra o haz clic para subir',
    colorPresets: 'Presets Rápidos',
    customColors: 'O personaliza',
    primaryColor: 'Principal',
    bgColor: 'Fondo',
    accentColor: 'Acento',
    next: 'Continuar',
    back: 'Atrás',
    submit: 'Enviar Solicitud',
    submitting: 'Enviando...',
    successTitle: '¡Solicitud Enviada!',
    successText: 'Prepararemos tu plantilla personalizada y te contactaremos en 24-48 horas.',
    successCta: 'Listo',
    errorText: 'Algo salió mal. Por favor intenta de nuevo.',
    step: 'Paso',
    of: 'de'
  },
  ar: {
    step1Title: 'ما نوع الموقع الذي تحتاجه؟',
    step1Subtitle: 'اختر الخيار الأنسب لمشروعك',
    step2Title: 'أخبرنا عن علامتك التجارية',
    step2Subtitle: 'سننشئ تصميمًا مخصصًا لك',
    step3Title: 'اختر أسلوبك',
    step3Subtitle: 'اختر الألوان التي تمثل علامتك التجارية',
    brandLabel: 'اسم العلامة التجارية / الشركة',
    brandPlaceholder: 'أدخل اسم عملك',
    emailLabel: 'البريد الإلكتروني',
    emailPlaceholder: 'you@company.com',
    phoneLabel: 'الهاتف (اختياري)',
    phonePlaceholder: '+966 50 000 0000',
    descLabel: 'أخبرنا المزيد (اختياري)',
    descPlaceholder: 'ماذا يفعل عملك؟ من هو جمهورك المستهدف؟',
    logoLabel: 'رفع الشعار (اختياري)',
    logoHint: 'PNG أو JPG أو SVG - حد أقصى 5MB',
    dragDrop: 'اسحب وأفلت أو انقر للرفع',
    colorPresets: 'قوالب سريعة',
    customColors: 'أو خصص',
    primaryColor: 'الرئيسي',
    bgColor: 'الخلفية',
    accentColor: 'التمييز',
    next: 'متابعة',
    back: 'رجوع',
    submit: 'إرسال الطلب',
    submitting: 'جاري الإرسال...',
    successTitle: 'تم إرسال طلبك!',
    successText: 'سنعد قالبك المخصص ونتواصل معك خلال 24-48 ساعة.',
    successCta: 'تم',
    errorText: 'حدث خطأ ما. يرجى المحاولة مرة أخرى.',
    step: 'خطوة',
    of: 'من'
  },
  zh: {
    step1Title: '您需要什么类型的网站？',
    step1Subtitle: '选择最适合您项目的选项',
    step2Title: '告诉我们您的品牌',
    step2Subtitle: '我们将为您创建定制设计',
    step3Title: '选择您的风格',
    step3Subtitle: '选择代表您品牌的颜色',
    brandLabel: '品牌/公司名称',
    brandPlaceholder: '输入您的企业名称',
    emailLabel: '电子邮箱',
    emailPlaceholder: 'you@company.com',
    phoneLabel: '电话（可选）',
    phonePlaceholder: '+86 138 0000 0000',
    descLabel: '告诉我们更多（可选）',
    descPlaceholder: '您的企业做什么？目标受众是谁？',
    logoLabel: '上传标志（可选）',
    logoHint: 'PNG、JPG 或 SVG - 最大 5MB',
    dragDrop: '拖放或点击上传',
    colorPresets: '快速预设',
    customColors: '或自定义',
    primaryColor: '主色',
    bgColor: '背景',
    accentColor: '强调',
    next: '继续',
    back: '返回',
    submit: '提交请求',
    submitting: '发送中...',
    successTitle: '请求已提交！',
    successText: '我们将准备您的定制模板，并在24-48小时内与您联系。',
    successCta: '完成',
    errorText: '出了点问题。请重试。',
    step: '步骤',
    of: '/'
  }
};

export default function TemplateRequestModal({ isOpen, onClose }) {
  const { language } = useLanguage();
  const t = getTranslation(translations, language);
  const isTurkish = language === 'tr';
  
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    websiteType: '',
    brandName: '',
    email: '',
    phone: '',
    description: '',
    colors: { primary: '#38BDF8', background: '#030303', accent: '#0EA5E9' }
  });
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.size <= 5 * 1024 * 1024) {
      setLogo(file);
      const reader = new FileReader();
      reader.onload = (ev) => setLogoPreview(ev.target?.result);
      reader.readAsDataURL(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.size <= 5 * 1024 * 1024) {
        setLogo(file);
        const reader = new FileReader();
        reader.onload = (ev) => setLogoPreview(ev.target?.result);
        reader.readAsDataURL(file);
      }
    }
  };

  const applyPreset = (preset) => {
    setFormData(prev => ({
      ...prev,
      colors: { primary: preset.primary, background: preset.background, accent: preset.accent }
    }));
  };

  const resetForm = () => {
    setStep(1);
    setFormData({
      websiteType: '',
      brandName: '',
      email: '',
      phone: '',
      description: '',
      colors: { primary: '#38BDF8', background: '#030303', accent: '#0EA5E9' }
    });
    setLogo(null);
    setLogoPreview(null);
    setSuccess(false);
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!formData.brandName.trim() || !formData.email.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const submitData = new FormData();
      submitData.append('brandName', formData.brandName);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('description', formData.description);
      submitData.append('websiteType', formData.websiteType);
      submitData.append('colors', JSON.stringify(formData.colors));
      if (logo) submitData.append('logo', logo);

      const response = await fetch(`${API_URL}/api/template-request`, {
        method: 'POST',
        body: submitData
      });

      if (!response.ok) throw new Error('Request failed');
      setSuccess(true);
    } catch (err) {
      setError(t.errorText);
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return formData.websiteType !== '';
    if (step === 2) return formData.brandName.trim() !== '' && formData.email.trim() !== '';
    return true;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Premium Background */}
      <div className="absolute inset-0 bg-[#030303]/95 backdrop-blur-2xl" onClick={handleClose} />
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-[#38BDF8]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-cyan-500/10 rounded-full blur-[150px]" />
      </div>
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative w-full max-w-2xl bg-gradient-to-b from-[#0c0c0c] to-[#080808] border border-white/[0.08] rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
      >
        {/* Decorative gradient */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#38BDF8] to-transparent" />
        
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-5 right-5 z-10 w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-all"
        >
          <X size={20} />
        </button>

        <AnimatePresence mode="wait">
          {success ? (
            /* Success State */
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="p-12 text-center"
            >
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.2 }}
                className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-8"
              >
                <Check size={48} className="text-emerald-400" />
              </motion.div>
              <h2 className="text-3xl font-bold text-white mb-4">{t.successTitle}</h2>
              <p className="text-white/50 text-lg mb-8 max-w-md mx-auto">{t.successText}</p>
              <button
                onClick={handleClose}
                className="px-8 py-4 bg-[#38BDF8] text-black font-semibold rounded-xl hover:bg-white transition-colors"
              >
                {t.successCta}
              </button>
            </motion.div>
          ) : (
            /* Form Steps */
            <div className="p-8">
              {/* Progress Bar */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-white/40 text-sm font-medium">
                    {t.step} {step} {t.of} 3
                  </span>
                  <div className="flex gap-2">
                    {[1, 2, 3].map((s) => (
                      <div
                        key={s}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${
                          s === step ? 'bg-[#38BDF8] w-6' : s < step ? 'bg-[#38BDF8]' : 'bg-white/20'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-[#38BDF8] to-cyan-400"
                    initial={{ width: '0%' }}
                    animate={{ width: `${(step / 3) * 100}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>

              <AnimatePresence mode="wait">
                {/* Step 1: Website Type */}
                {step === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                  >
                    <div className="text-center mb-8">
                      <h2 className="text-2xl font-bold text-white mb-2">{t.step1Title}</h2>
                      <p className="text-white/40">{t.step1Subtitle}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      {websiteTypes.map((type) => {
                        const Icon = type.icon;
                        const isSelected = formData.websiteType === type.id;
                        return (
                          <button
                            key={type.id}
                            onClick={() => setFormData({ ...formData, websiteType: type.id })}
                            className={`group p-6 rounded-2xl border-2 transition-all duration-300 text-left ${
                              isSelected
                                ? 'border-[#38BDF8] bg-[#38BDF8]/10'
                                : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
                            }`}
                          >
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                              isSelected ? 'bg-[#38BDF8]/20 text-[#38BDF8]' : 'bg-white/5 text-white/40 group-hover:text-white/60'
                            }`}>
                              <Icon size={24} />
                            </div>
                            <h3 className={`font-semibold text-lg ${isSelected ? 'text-white' : 'text-white/70'}`}>
                              {isTurkish ? type.labelTr : type.labelEn}
                            </h3>
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Brand Info */}
                {step === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                  >
                    <div className="text-center mb-8">
                      <h2 className="text-2xl font-bold text-white mb-2">{t.step2Title}</h2>
                      <p className="text-white/40">{t.step2Subtitle}</p>
                    </div>

                    {/* Brand Name */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-white/60 mb-2">
                        <Building2 size={16} />
                        {t.brandLabel} *
                      </label>
                      <input
                        type="text"
                        value={formData.brandName}
                        onChange={(e) => setFormData({ ...formData, brandName: e.target.value })}
                        placeholder={t.brandPlaceholder}
                        className="w-full px-4 py-4 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#38BDF8]/50 focus:bg-white/[0.05] transition-all"
                      />
                    </div>

                    {/* Email & Phone */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-white/60 mb-2">
                          <Mail size={16} />
                          {t.emailLabel} *
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder={t.emailPlaceholder}
                          className="w-full px-4 py-4 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#38BDF8]/50 transition-all"
                        />
                      </div>
                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-white/60 mb-2">
                          <Phone size={16} />
                          {t.phoneLabel}
                        </label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder={t.phonePlaceholder}
                          className="w-full px-4 py-4 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#38BDF8]/50 transition-all"
                        />
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-white/60 mb-2">
                        <FileText size={16} />
                        {t.descLabel}
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder={t.descPlaceholder}
                        rows={3}
                        className="w-full px-4 py-4 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#38BDF8]/50 transition-all resize-none"
                      />
                    </div>

                    {/* Logo Upload */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-white/60 mb-2">
                        <Upload size={16} />
                        {t.logoLabel}
                      </label>
                      <div
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all ${
                          dragActive
                            ? 'border-[#38BDF8] bg-[#38BDF8]/5'
                            : logoPreview
                            ? 'border-emerald-500/30 bg-emerald-500/5'
                            : 'border-white/10 hover:border-white/20 bg-white/[0.02]'
                        }`}
                      >
                        {logoPreview ? (
                          <div className="flex items-center justify-center gap-4">
                            <img src={logoPreview} alt="Logo" className="max-h-16 max-w-32 object-contain" />
                            <button
                              type="button"
                              onClick={() => { setLogo(null); setLogoPreview(null); }}
                              className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                            >
                              <X size={18} />
                            </button>
                          </div>
                        ) : (
                          <label className="cursor-pointer block">
                            <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
                            <Upload size={32} className="mx-auto mb-3 text-white/30" />
                            <p className="text-white/50 text-sm">{t.dragDrop}</p>
                            <p className="text-white/30 text-xs mt-1">{t.logoHint}</p>
                          </label>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Colors */}
                {step === 3 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                  >
                    <div className="text-center mb-8">
                      <h2 className="text-2xl font-bold text-white mb-2">{t.step3Title}</h2>
                      <p className="text-white/40">{t.step3Subtitle}</p>
                    </div>

                    {/* Color Presets */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-white/60 mb-3">
                        <Palette size={16} />
                        {t.colorPresets}
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        {colorPresets.map((preset) => {
                          const isSelected = formData.colors.primary === preset.primary;
                          return (
                            <button
                              key={preset.name}
                              onClick={() => applyPreset(preset)}
                              className={`p-4 rounded-xl border-2 transition-all ${
                                isSelected
                                  ? 'border-[#38BDF8] bg-white/5'
                                  : 'border-white/10 hover:border-white/20 bg-white/[0.02]'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <div
                                  className="w-8 h-8 rounded-lg"
                                  style={{ backgroundColor: preset.primary }}
                                />
                                <span className="text-white/70 text-sm font-medium">{preset.name}</span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Custom Colors */}
                    <div>
                      <label className="text-sm font-medium text-white/60 mb-3 block">{t.customColors}</label>
                      <div className="grid grid-cols-3 gap-4">
                        {[
                          { key: 'primary', label: t.primaryColor },
                          { key: 'background', label: t.bgColor },
                          { key: 'accent', label: t.accentColor }
                        ].map(({ key, label }) => (
                          <div key={key} className="space-y-2">
                            <span className="text-xs text-white/40 block text-center">{label}</span>
                            <div className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white/[0.03] border border-white/10">
                              <input
                                type="color"
                                value={formData.colors[key]}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  colors: { ...formData.colors, [key]: e.target.value }
                                })}
                                className="w-16 h-16 rounded-xl cursor-pointer border-2 border-white/10 hover:border-white/30 transition-colors"
                                style={{ backgroundColor: formData.colors[key] }}
                              />
                              <input
                                type="text"
                                value={formData.colors[key]}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  colors: { ...formData.colors, [key]: e.target.value }
                                })}
                                className="w-full text-center bg-white/[0.05] px-2 py-1.5 rounded-lg text-white text-xs font-mono focus:outline-none focus:bg-white/[0.08] transition-colors"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Preview */}
                    <div className="p-6 rounded-2xl border border-white/10" style={{ backgroundColor: formData.colors.background }}>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl" style={{ backgroundColor: formData.colors.primary }} />
                        <div className="flex-1">
                          <div className="h-3 w-32 rounded-full mb-2" style={{ backgroundColor: formData.colors.primary }} />
                          <div className="h-2 w-48 rounded-full opacity-30" style={{ backgroundColor: formData.colors.accent }} />
                        </div>
                        <div className="px-4 py-2 rounded-lg text-sm font-medium" style={{ backgroundColor: formData.colors.accent, color: formData.colors.background }}>
                          Button
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Error */}
              {error && (
                <div className="mt-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  {error}
                </div>
              )}

              {/* Navigation */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
                {step > 1 ? (
                  <button
                    onClick={() => setStep(step - 1)}
                    className="flex items-center gap-2 px-6 py-3 text-white/60 hover:text-white transition-colors"
                  >
                    <ArrowLeft size={18} />
                    {t.back}
                  </button>
                ) : (
                  <div />
                )}

                {step < 3 ? (
                  <button
                    onClick={() => canProceed() && setStep(step + 1)}
                    disabled={!canProceed()}
                    className={`flex items-center gap-2 px-8 py-4 rounded-xl font-semibold transition-all ${
                      canProceed()
                        ? 'bg-[#38BDF8] text-black hover:bg-white'
                        : 'bg-white/10 text-white/30 cursor-not-allowed'
                    }`}
                  >
                    {t.next}
                    <ArrowRight size={18} />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={loading || !canProceed()}
                    className={`flex items-center gap-2 px-8 py-4 rounded-xl font-semibold transition-all ${
                      loading || !canProceed()
                        ? 'bg-white/10 text-white/30 cursor-not-allowed'
                        : 'bg-[#38BDF8] text-black hover:bg-white'
                    }`}
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                        {t.submitting}
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        {t.submit}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
